import { useEffect, useRef, useState } from 'react'

import {
  Anchor,
  Button,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
} from '@mantine/core'

export interface HoldApprovalControlProps {
  onApprove?: () => Promise<void>
  onStartApprove?: () => void
  onCancel?: () => void
  onCompleted?: () => void
  disabled?: boolean
  buttonText?: string
  successText?: string
  cycleDurationMs?: number
  holdDurationMs?: number
  error?: string | null
  setError?: (error: string | null) => void
}

export type HoldApprovalPhase = 'initial' | 'processing' | 'complete' | 'error'

export function HoldApprovalControl({
  onApprove,
  onStartApprove,
  onCancel,
  onCompleted,
  disabled = false,
  buttonText = 'Hold to Approve',
  successText = 'Approved successfully',
  holdDurationMs = 2000,
  cycleDurationMs = 2000,
  error,
  setError,
}: HoldApprovalControlProps) {
  const [phase, setPhase] = useState<HoldApprovalPhase>('initial')
  const [holdActive, setHoldActive] = useState(false)
  const holdTimerRef = useRef<number | null>(null)
  const [cycling, setCycling] = useState(false)
  const [cycleProgress, setCycleProgress] = useState(100)

  const startHold = (): void => {
    if (disabled || phase !== 'initial') return

    if (setError) {
      setError(null)
    }

    setHoldActive(true)

    if (onStartApprove) {
      onStartApprove()
    }

    holdTimerRef.current = window.setTimeout(() => {
      clearTimers()
      void handleConfirmation()
    }, holdDurationMs)
  }

  const clearTimers = (): void => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
  }

  const cancelHold = (): void => {
    clearTimers()
    setHoldActive(false)

    if (onCancel) {
      onCancel()
    }
  }

  const handleTryAgain = (event: React.MouseEvent): void => {
    if (event instanceof MouseEvent) {
      event.preventDefault()
    }

    setHoldActive(false)
    setPhase('initial')
  }

  useEffect(() => {
    if (cycling && cycleDurationMs > 50) {
      setCycleProgress(100)
      setTimeout(() => {
        setCycleProgress(0)
      }, 50)
      const cycleTimer = setTimeout(() => {
        setPhase('initial')
        setCycling(false)

        if (onCompleted) {
          onCompleted()
        }
      }, cycleDurationMs)
      return () => {
        clearTimeout(cycleTimer)
      }
    }
  }, [cycleDurationMs, cycling, onCompleted])

  const handleConfirmation = async (): Promise<void> => {
    setPhase('processing')

    if (onApprove) {
      try {
        await onApprove()
      } catch (err) {
        console.error('Error approving:', err)
        if (setError) {
          setError('An error occurred while processing your request.')
        }
        setPhase('error')
        return
      }
    } else {
      console.error('onApprove function is not provided')
    }

    setPhase('complete')
    setCycling(true)
    setHoldActive(false)
  }

  useEffect(() => {
    return () => {
      clearTimers()
    }
  }, [])

  return (
    <Stack gap="0">
      {phase === 'initial' && (
        <Button
          onMouseDown={startHold}
          onMouseUp={cancelHold}
          onMouseLeave={cancelHold}
          onTouchStart={startHold}
          onTouchEnd={cancelHold}
          disabled={disabled}
          style={{
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: holdActive ? '100%' : '0%',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              transformOrigin: 'left',
              transform: 'scaleX(0)',
              animation: holdActive
                ? `progressAnimation ${holdDurationMs}ms ease-out forwards`
                : 'none',
              zIndex: 1,
            }}
          />
          {holdActive && (
            <style>
              {`
                @keyframes progressAnimation {
                  0% { transform: scaleX(0); }
                  100% { transform: scaleX(1); }
                }
              `}
            </style>
          )}
          <Text style={{ position: 'relative', zIndex: 2 }}>{buttonText}</Text>
        </Button>
      )}

      {phase === 'initial' && (
        <Text c="dimmed" size="xs" ta="center" mt="xxxs">
          Hold to confirm
        </Text>
      )}

      {phase === 'processing' && (
        <Group>
          <Loader size="sm" />
          <Text size="sm">Processing...</Text>
        </Group>
      )}

      {phase === 'complete' && (
        <Stack gap="xs">
          <Text c="green" size="sm">
            {successText}
          </Text>
          <Progress
            value={cycleProgress}
            size="xs"
            color="gray"
            transitionDuration={cycleDurationMs}
          />
        </Stack>
      )}

      {phase === 'error' && (
        <Stack gap="xs" ta="center">
          <Text c="red" size="md">
            {error || 'There was an error processing your request.'}
          </Text>
          <Anchor href="#" onClick={handleTryAgain}>
            Try Again
          </Anchor>
        </Stack>
      )}
    </Stack>
  )
}
